import { useState, useEffect } from 'react'
import { Modal } from 'components/UI/Modal/Modal'
import FbButton from 'components/FbUI/FbButton'
import { SalesStage } from 'models/sales_stages'
import { StageDragAndDropItem } from './StageDragAndDropItem'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { restrictToVerticalAxis } from '@dnd-kit/modifiers'
import { ArrowUpDown } from 'lucide-react'
import { Switch } from 'components/UI/Switch/Switch'
import { usePreferences } from 'context/preferences/PreferencesContext'
import apiService from 'services/api'
import { toast } from 'react-toastify'
import { useQueryClient } from '@tanstack/react-query'

interface ReorderStagesModalProps {
  stages: SalesStage[]
  onSave: (orderedStageIds: number[]) => Promise<void>
}

export function ReorderStagesModal({
  stages,
  onSave,
}: ReorderStagesModalProps) {
  const [open, setOpen] = useState(false)
  const [orderedStages, setOrderedStages] = useState<SalesStage[]>([])
  const [isSaving, setIsSaving] = useState(false)
  const { isSettingEnabled } = usePreferences()
  const api = apiService()
  const { mutate: changeMiscSettings } = api.useChangeMiscSettings()
  const queryClient = useQueryClient()

  const customOrderEnabled = isSettingEnabled('sales_stage_custom_order')

  const handleToggleCustomOrder = () => {
    changeMiscSettings({
      key: 'sales_stage_custom_order',
      value: customOrderEnabled ? 'false' : 'true',
    })
  }

  // Re-sort stages when custom order setting changes
  useEffect(() => {
    if (open && orderedStages.length > 0) {
      const sortedStages = [...orderedStages].sort((a, b) =>
        customOrderEnabled
          ? a.order - b.order
          : b.avg_days_to_close - a.avg_days_to_close
      )
      setOrderedStages(sortedStages)
    }
  }, [customOrderEnabled, open])

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  const handleOpen = () => {
    // Initialize with current order
    setOrderedStages([...stages])
    setOpen(true)
  }

  const handleClose = () => {
    setOpen(false)
    setOrderedStages([])
  }

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (over && active.id !== over.id) {
      const oldIndex = orderedStages.findIndex((s) => s.id === active.id)
      const newIndex = orderedStages.findIndex((s) => s.id === over.id)
      const newOrderedStages = [...orderedStages]
      const [movedItem] = newOrderedStages.splice(oldIndex, 1)
      newOrderedStages.splice(newIndex, 0, movedItem)
      setOrderedStages(newOrderedStages)
    }
  }

  const moveStageUp = (index: number) => {
    if (index > 0) {
      const newOrderedStages = [...orderedStages]
      const temp = newOrderedStages[index]
      newOrderedStages[index] = newOrderedStages[index - 1]
      newOrderedStages[index - 1] = temp
      setOrderedStages(newOrderedStages)
    }
  }

  const moveStageDown = (index: number) => {
    if (index < orderedStages.length - 1) {
      const newOrderedStages = [...orderedStages]
      const temp = newOrderedStages[index]
      newOrderedStages[index] = newOrderedStages[index + 1]
      newOrderedStages[index + 1] = temp
      setOrderedStages(newOrderedStages)
    }
  }

  const handleSave = async () => {
    setIsSaving(true)
    try {
      const orderedIds = orderedStages.map((s) => s.id)
      await onSave(orderedIds)
      queryClient.invalidateQueries({ queryKey: ['preferences'] })
      handleClose()
    } finally {
      setIsSaving(false)
    }
  }

  const handleResetToDefault = async () => {
    setIsSaving(true)
    try {
      // Sort by win_rate ASC, treating null/undefined as 0 (same as migration)
      const defaultOrderedStages = [...orderedStages].sort(
        (a, b) => (a.win_rate ?? 0) - (b.win_rate ?? 0)
      )
      const orderedIds = defaultOrderedStages.map((s) => s.id)

      // Save the default order
      await onSave(orderedIds)

      // Disable custom ordering
      changeMiscSettings({
        key: 'sales_stage_custom_order',
        value: 'false',
      })

      toast.info('Sales stages reset to default order')
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <>
      <FbButton variant="secondary" onClick={handleOpen}>
        <ArrowUpDown size={16} className="mr-2" />
        Reorder Stages
      </FbButton>
      <Modal
        open={open}
        onOpenChange={(isOpen) => !isOpen && handleClose()}
        title="Reorder Sales Stages"
        description="Once manual ordering is enabled, drag and drop sales stages to reorder them. The custom stage order will apply across deal cards, the deals table, and the sales pipeline. You can revert to default settings at any time."
        onAccept={handleSave}
        acceptButtonText="Save Order"
        cancelButtonText="Cancel"
        loading={isSaving}
        size="md"
        blockAccept={!customOrderEnabled}
      >
        <hr className="mb-3" />
        <div className="flex items-center justify-between mb-4">
          <span className="text-sm text-gray-600">
            Enable custom ordering to manually arrange stages
          </span>
          <div className="flex items-center gap-3">
            <Switch
              checked={customOrderEnabled}
              onChange={handleToggleCustomOrder}
            />
          </div>
        </div>
        <div className="border border-gray-300 rounded-md p-2">
          <div className="max-h-[400px] overflow-y-auto">
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
              modifiers={[restrictToVerticalAxis]}
              autoScroll={{
                threshold: {
                  x: 0,
                  y: 0.2,
                },
                acceleration: 15,
                interval: 5,
              }}
            >
              <SortableContext
                items={orderedStages.map((s) => s.id)}
                strategy={verticalListSortingStrategy}
              >
                {orderedStages.map((stage, index) => (
                  <StageDragAndDropItem
                    key={stage.id}
                    id={stage.id}
                    stage={stage}
                    onMoveUp={() => moveStageUp(index)}
                    onMoveDown={() => moveStageDown(index)}
                    isFirst={index === 0}
                    isLast={index === orderedStages.length - 1}
                    disabled={!customOrderEnabled}
                  />
                ))}
              </SortableContext>
            </DndContext>
          </div>
        </div>
        <div className="flex items-center justify-end mt-2 mb-2">
          <FbButton
            variant="secondary"
            size="sm"
            onClick={handleResetToDefault}
            disabled={isSaving}
          >
            Reset to Default
          </FbButton>
        </div>
      </Modal>
    </>
  )
}
